import { CategoryReference, TagCategory } from 'apprise-core/category';
import { LifecycleEvent } from 'apprise-core/common';
import { TagReference } from 'apprise-core/tag';
import { Optional } from 'apprise-core/utils';
import { usePreload } from 'apprise-frontend-core/client/preload';
import { useCategoryClientCache } from 'apprise-frontend-tags/category/context';
import { categoryPlural, categorySingular } from 'apprise-frontend-tags/constants';
import { useCrud } from 'apprise-ui/utils/crudtask';
import { tagcategoryapi, useCategoryCalls } from './client';
import { useCategoryModel } from './model';


export const useCategoryStore = () => {

    const state = useCategoryClientCache()

    const preload = usePreload()

    const crud = useCrud({ singular: categorySingular, plural: categoryPlural })

    const model = useCategoryModel()
    const calls = useCategoryCalls()

    const self = {


        allCategories: () => state.get().all

        ,

        allCategoriesOf: (type: Optional<string>) => type ? self.allCategories().filter(c => c.type === type) : []

        ,

        allActiveCategoriesOf: (type: Optional<string>) => type ? self.allCategoriesOf(type).filter(c => c.lifecycle.state === 'active') : []

        ,

        lookupCategory: (category: Optional<CategoryReference>) => category ? self.allCategories().find(t => t.id === category) : undefined

        ,

        safeLookupCategory: (category: Optional<CategoryReference>) => self.lookupCategory(category) ?? model.noCategory(category)

        ,

        fetchAllCategories: crud.fetchAllWith(async () => {

            const all = await (preload.get<TagCategory[]>(tagcategoryapi) ?? calls.fetchAllCategories())

            state.set(s => s.all = all)

            return all

        }).done()

        ,

        saveCategory: crud.saveOneWith(async (category: TagCategory) => {

            const isNew = !category.lifecycle.created

            const saved = isNew ? await calls.addCategory(category) : await calls.updateCategory(category)

            state.set(s => s.all = isNew ? [saved, ...s.all] : s.all.map(t => t.id === saved.id ? saved : t))

            return saved

        }).done()

        ,

        saveCategories: crud.saveManyWith(async (categories: TagCategory[]) => {

            const saved = await calls.updateCategories(categories)

            state.set(s => s.all = s.all.map(c => saved.find(cc => cc.id === c.id) ?? c))

            return saved

        }).done()

        ,

        removeCategory: crud.removeOneWith(async (category: TagCategory, onConfirm?: () => void) => {

            await calls.deleteCategory(category)

            state.set(s => s.all = s.all.filter(c => c.id !== category.id))

            onConfirm?.()

        }).withConsent()

        ,

        push: (event: LifecycleEvent<TagCategory>) => {

            const { subject: pushed } = event

            state.set(s => {

                switch (event.type) {

                    case 'add': s.all = [...self.allCategories(), pushed]; break;
                    case 'remove': s.all = s.all.filter(c => c.id !== pushed.id); break;
                    case 'change': s.all = s.all.map(c => c.id === pushed.id ? pushed : c); break;

                }

            })

        }

        ,

        allDefaults: () => self.allCategories().flatMap(c => c.properties.field.default).filter(e => !!e) as TagReference[]

    }

    return self

}